import React, { FC, useContext, useRef, useState } from 'react'
import { Button, Modal, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import DialogContext from '../Authentication/DialogContext'
import { hideDialog } from '../utils/DialogUtil'
import DraggableModalDialog from './DraggableModalDialog'
import FormFields, { FormDataCheckbox, FormDataControl, FormDataFile } from './FormFields'

const EditDialog: FC<{ editDialogProps: EditDialogProps }> = ({ editDialogProps: props }) => {
  const { t } = useTranslation()
  const {
    show,
    backdrop = 'static',
    header = t('editDialog.header'),
    showCloseButton = true,
    submitText = t('submit'),
    showCancelButton = true,
    cancelText = t('cancel'),
    footerPrefix,
    ...remainingProps
  } = props

  const [submitting, setSubmitting] = useState(false)
  const { editDialogPropsSetter } = useContext(DialogContext)

  const childRef = useRef<HTMLButtonElement>(null)

  const submit = () => {
    if (childRef != null && childRef.current != null) {
      childRef.current.click()
    }
  }

  return (
    <Modal
      dialogAs={DraggableModalDialog}
      show={show}
      onHide={() => hideDialog(editDialogPropsSetter)}
      backdrop={backdrop}
      size='lg'
      aria-labelledby='contained-modal-title-vcenter'
      centered
      className='modal-dark'>
      <Modal.Header closeButton={showCloseButton}>
        <Modal.Title id='contained-modal-title-vcenter'>{header}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormFields header={header} setSubmitting={setSubmitting} forwardedRef={childRef} {...remainingProps} />
      </Modal.Body>
      <Modal.Footer>
        {footerPrefix}
        <Button onClick={submit} disabled={submitting} block={true} className='w-auto'>
          {submitting ? (
            <>
              <Spinner as='span' animation='border' size='sm' role='status' aria-hidden='true' />
              <span className='ml-1'>{t('submitting')}</span>
            </>
          ) : (
            submitText
          )}
        </Button>
        {showCancelButton && (
          <Button variant='outline-danger' onClick={() => hideDialog(editDialogPropsSetter)}>
            {cancelText}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

export type EditDialogProps = {
  show: boolean
  backdrop?: 'static' | true | false
  header?: string | JSX.Element
  showCloseButton?: boolean
  data: (FormDataControl | FormDataCheckbox | FormDataFile)[] | ((custom: string[]) => (FormDataControl | FormDataCheckbox | FormDataFile)[])
  initCustom?: string[]
  labelGridSize?: number

  onSubmit: (...args: any[]) => Promise<string | undefined>
  onSubmitExtraArgs?: any[]
  canForceUpdate?: boolean
  handleResponseCustom?: (message: any) => void
  submitText?: string
  showCancelButton?: boolean
  cancelText?: string
  footerPrefix?: JSX.Element
}

export default EditDialog
